import { PDF_FORM_FIELD_TYPE, PdfAnnotationSubtype } from "@embedpdf/models";
import type { PdfAnnotationObject } from "@embedpdf/models";
import type { AnnotationScope, AnnotationTransferItem } from "@embedpdf/plugin-annotation";
import type { FormScope } from "@embedpdf/plugin-form";
import type { HistoryScope } from "@embedpdf/plugin-history";
import type { PdfDocState } from "./pdf-storage/types";

export interface PdfReplayTarget {
  annotation: AnnotationScope;
  form: FormScope;
  history: HistoryScope;
  isReplaying: boolean;
  replayGen: number;
  saveTimerId: ReturnType<typeof setTimeout> | null;
  annotationReady: boolean;
  annotationReadyWaiters: Array<() => void>;
  replayQueue: Promise<void>;
}

function waitForFormReady(form: FormScope): Promise<void> {
  if (form.getFormFields().length > 0) return Promise.resolve();
  return new Promise<void>((resolve) => {
    const unsub = form.onFormReady(() => { unsub(); resolve(); });
  });
}

/**
 * Import annotations into the viewer, giving ROTATED STAMPS the two-step
 * treatment: create unrotated at `unrotatedRect`, then apply the rotation
 * as an update. One-step import of a pre-rotated stamp makes the engine
 * regenerate the appearance against the rotated AABB and the artwork comes
 * back at the wrong scale (engine-verified: only create-then-rotate
 * reproduces the live render exactly). Everything else goes through the
 * plugin's bulk import during hydration, or native create commands for AI history.
 */
function importItems(loaded: PdfReplayTarget, items: AnnotationTransferItem[], preserveHistory = false): void {
  const bulk: AnnotationTransferItem[] = [];
  for (const item of items) {
    const anno = item.annotation as PdfAnnotationObject & {
      rotation?: number;
      unrotatedRect?: PdfAnnotationObject["rect"];
    };
    const isRotatedStamp =
      anno.type === PdfAnnotationSubtype.STAMP &&
      !!anno.rotation &&
      anno.rotation !== 0 &&
      !!anno.unrotatedRect;
    if (!isRotatedStamp) {
      if (preserveHistory) loaded.annotation.createAnnotation(anno.pageIndex, anno, item.ctx as never);
      else bulk.push(item);
      continue;
    }
    loaded.annotation.createAnnotation(
      anno.pageIndex,
      {
        ...anno,
        rotation: 0,
        rect: anno.unrotatedRect!,
        unrotatedRect: undefined,
      } as PdfAnnotationObject,
      item.ctx as never,
    );
    loaded.annotation.updateAnnotation(anno.pageIndex, anno.id, {
      rotation: anno.rotation,
      rect: anno.rect,
      unrotatedRect: anno.unrotatedRect,
    } as Partial<PdfAnnotationObject>);
  }
  if (bulk.length > 0) loaded.annotation.importAnnotations(bulk);
}

/**
 * Block until plugin-annotation emits `'loaded'` for this tab. Must be
 * awaited before the first `importAnnotations` call — otherwise items go
 * into the plugin's internal import queue and their `'create'` events fire
 * asynchronously whenever the initial `getAllAnnotations` task finally
 * resolves, which can be well after our replay window closed.
 */
function waitAnnotationReady(loaded: PdfReplayTarget): Promise<void> {
  if (loaded.annotationReady) return Promise.resolve();
  return new Promise<void>((resolve) => {
    loaded.annotationReadyWaiters.push(resolve);
  });
}

/**
 * Push a PdfDocState into the viewer. Centralizes the hydrate + restore
 * paths so both get:
 *
 * 1. A `replayGen` bump, so if a new replay starts before this one's
 *    finalizer runs, the stale finalizer bails instead of flipping
 *    `isReplaying=false` mid-import.
 * 2. The `annotationReady` wait, so `importAnnotations` processes
 *    synchronously and its `'create'` events land inside our replay
 *    window (where `isReplaying` blocks them) instead of queuing.
 * 3. An in-flight autosave cancellation — autosave will re-fire on the
 *    next real user event, reflecting the freshly-replayed state.
 * 4. A history purge on hydration/restore only. Streaming AI changes
 *    retain native commands and prior manual history.
 */
export async function runReplay(
  loaded: PdfReplayTarget,
  state: PdfDocState,
  options: {
    wipeFirst: boolean;
    /**
     * Annotation ids the reconcile must NOT delete even when they're absent
     * from `state`. Used when applying an AI turn's doc_state: overlays the
     * user drew AFTER the turn started aren't in the AI's state, and blindly
     * deleting "everything not in incoming" would clobber them. See
     * applyDocState's base-diff.
     */
    preserveIds?: Set<string>;
    skipAnnotationIds?: Set<string>;
    /**
     * Form field names the AI turn did not touch. When merging an AI
     * doc_state we only overwrite the fields the AI actually changed, so a
     * value the user typed mid-turn survives.
     */
    skipFormValues?: Set<string>;
    /** Streaming edits use the plugins' native undo commands. Hydration resets them. */
    preserveHistory?: boolean;
  },
): Promise<void> {
  loaded.replayGen++;
  const myGen = loaded.replayGen;
  loaded.isReplaying = true;

  if (loaded.saveTimerId) {
    clearTimeout(loaded.saveTimerId);
    loaded.saveTimerId = null;
  }

  try {
    await waitAnnotationReady(loaded);
    await settleAnnotationCommits(loaded.annotation);

    const incoming = state.annotations.filter(
      (item) => item.annotation.type !== PdfAnnotationSubtype.WIDGET,
    ) as AnnotationTransferItem[];

    if (options.wipeFirst) {
      // Update in place so the backend and human references keep the same
      // identity. Replacement is reserved for type/page changes, and only
      // imports after the engine has completed deletion of that identity.
      const tracked = loaded.annotation
        .getAnnotations()
        .filter((t) => t.object.type !== PdfAnnotationSubtype.WIDGET && t.commitState !== "deleted");
      const trackedById = new Map(tracked.map((t) => [t.object.id, t.object]));
      const incomingIds = new Set(incoming.map((item) => item.annotation.id));

      const preserveIds = options.preserveIds;
      const toDelete = tracked
        .filter(
          (t) =>
            !incomingIds.has(t.object.id) && !preserveIds?.has(t.object.id),
        )
        .map((t) => ({ pageIndex: t.object.pageIndex, id: t.object.id }));
      const toImport: AnnotationTransferItem[] = [];
      const replacements: AnnotationTransferItem[] = [];

      for (const item of incoming) {
        if (options.skipAnnotationIds?.has(item.annotation.id)) continue;
        const existing = trackedById.get(item.annotation.id);
        if (!existing) {
          toImport.push(item);
          continue;
        }
        const changed =
          JSON.stringify(existing) !== JSON.stringify(item.annotation);
        if (changed) {
          if (existing.pageIndex !== item.annotation.pageIndex || existing.type !== item.annotation.type) {
            toDelete.push({ pageIndex: existing.pageIndex, id: existing.id });
            replacements.push(item);
          } else {
            loaded.annotation.updateAnnotation(existing.pageIndex, existing.id, item.annotation);
            // autoCommit starts immediately. Do not mutate a pending creation
            // or update: its completion marks the whole identity synced.
            await settleAnnotationCommits(loaded.annotation);
          }
        }
      }

      if (toDelete.length > 0) {
        loaded.annotation.deleteAnnotations(toDelete);
        await settleAnnotationCommits(loaded.annotation);
      }
      toImport.push(...replacements);
      if (toImport.length > 0) importItems(loaded, toImport, options.preserveHistory);
    } else if (incoming.length > 0) {
      importItems(loaded, incoming, options.preserveHistory);
    }

    await settleAnnotationCommits(loaded.annotation);

    const formValues = options.skipFormValues
      ? Object.fromEntries(
          Object.entries(state.formValues).filter(
            ([name]) => !options.skipFormValues!.has(name),
          ),
        )
      : state.formValues;
    if (Object.keys(formValues).length > 0) {
      await waitForFormReady(loaded.form);
      const checkboxNames = new Set(
        loaded.form.getFormFields()
          .filter((field) => field.type === PDF_FORM_FIELD_TYPE.CHECKBOX)
          .map((field) => field.name),
      );
      // EmbedPDF imports every checkbox value except "Off" as checked.
      const replayValues = Object.fromEntries(
        Object.entries(formValues).map(([name, value]) => [
          name,
          value === "" && checkboxNames.has(name) ? "Off" : value,
        ]),
      );
      await loaded.form
        .setFormValues(replayValues)
        .toPromise()
        .catch((err) => {
          console.warn("[fillapp] setFormValues failed:", err);
        });
    }

    // Give any synchronously-dispatched events their microtask to flush
    // before we flip `isReplaying`. Cheap insurance, not load-bearing.
    await Promise.resolve();
  } finally {
    if (loaded.replayGen === myGen) {
      loaded.isReplaying = false;
      try {
        if (!options.preserveHistory) loaded.history.purgeByMetadata(() => true);
      } catch (err) {
        console.warn("[fillapp] purge history failed:", err);
      }
    }
  }
}


/** Auto-commit may already hold the lock, in which case commit() resolves early. */
export async function settleAnnotationCommits(annotation: AnnotationScope): Promise<void> {
  const deadline = Date.now() + 15_000;
  do {
    await annotation.commit().toPromise();
    const pending = annotation.getAnnotations().some((item) =>
      ["new", "dirty", "moved", "deleted"].includes(item.commitState),
    );
    if (!pending) return;
    if (Date.now() >= deadline) throw new Error("PDF annotation replay did not finish committing");
    await new Promise<void>((resolve) => setTimeout(resolve, 0));
  } while (true);
}

/** Queue the merge as well as the replay, so each result sees its predecessor. */
export function enqueuePdfReplay<T>(loaded: PdfReplayTarget, operation: () => Promise<T>): Promise<T> {
  const next = loaded.replayQueue.then(operation);
  loaded.replayQueue = next.then(() => undefined, () => undefined);
  return next;
}

export interface AiReplayTurn {
  previous: PdfDocState;
  expectedAnnotations: Map<string, PdfAnnotationObject>;
  expectedFormValues: Record<string, string>;
}

export function createAiReplayTurn(base: PdfDocState, loaded?: PdfReplayTarget): AiReplayTurn {
  return {
    previous: base,
    expectedAnnotations: new Map(loaded
      ? loaded.annotation.getAnnotations().filter(item => item.commitState !== "deleted").map(item => [item.object.id, structuredClone(item.object)])
      : base.annotations.map((item) => [item.annotation.id, structuredClone(item.annotation)])),
    expectedFormValues: { ...(loaded?.form.getFormValues() ?? base.formValues) },
  };
}

/** Preserve manual additions, edits and deletions, including same-entity AI conflicts. */
export async function applyAiReplay(loaded: PdfReplayTarget, state: PdfDocState, turn?: AiReplayTurn): Promise<void> {
  await settleAnnotationCommits(loaded.annotation);
  const preserveIds = new Set<string>();
  const skipAnnotationIds = new Set<string>();
  const skipFormValues = new Set<string>();
  if (turn) {
    const previous = new Map(turn.previous.annotations.map((item) => [item.annotation.id, item.annotation]));
    const incoming = new Map(state.annotations.map((item) => [item.annotation.id, item.annotation]));
    for (const [id, annotation] of incoming) {
      // Plugin defaults differ from the sidecar. Replaying an unchanged item
      // would otherwise create a native update command with nothing to undo.
      if (JSON.stringify(annotation) === JSON.stringify(previous.get(id))) skipAnnotationIds.add(id);
    }
    const live = new Map(loaded.annotation.getAnnotations()
      .filter((item) => item.object.type !== PdfAnnotationSubtype.WIDGET && item.commitState !== "deleted")
      .map((item) => [item.object.id, item.object]));
    for (const [id, object] of live) {
      if (!previous.has(id) && !incoming.has(id)) preserveIds.add(id);
      if (JSON.stringify(object) !== JSON.stringify(turn.expectedAnnotations.get(id))) {
        preserveIds.add(id);
        skipAnnotationIds.add(id);
      }
    }
    for (const [id] of previous) {
      if (!live.has(id) && turn.expectedAnnotations.has(id)) skipAnnotationIds.add(id);
    }
    for (const [name, value] of Object.entries(state.formValues)) {
      if (turn.previous.formValues[name] === value ||
        (loaded.form.getFormValues()[name] ?? "") !== (turn.expectedFormValues[name] ?? "")) skipFormValues.add(name);
    }
  }
  await runReplay(loaded, state, { wipeFirst: true, preserveIds, skipAnnotationIds, skipFormValues, preserveHistory: true });
  if (turn) {
    turn.previous = state;
    const expectedBeforeReplay = turn.expectedAnnotations;
    turn.expectedAnnotations = new Map(loaded.annotation.getAnnotations()
      .filter((item) => item.object.type !== PdfAnnotationSubtype.WIDGET && item.commitState !== "deleted")
      .map((item) => [item.object.id, item.object]));
    for (const id of skipAnnotationIds) {
      const expected = expectedBeforeReplay.get(id);
      if (expected) turn.expectedAnnotations.set(id, expected);
    }
    const expectedValues = { ...loaded.form.getFormValues() };
    for (const name of skipFormValues) expectedValues[name] = turn.expectedFormValues[name];
    turn.expectedFormValues = expectedValues;
  }
}
